'use strict';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import {mapPropsStream} from 'recompose';
import {Observable} from 'rxjs';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/withLatestFrom';
import {buildInjector} from '../../../_libs/angular-utils';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {GlobalEvents} from '../../../core-utilities/enums/GlobalEvents';
import {buildInit$, epicErrorHandler} from '../../../core-utilities/rx-utils';
import {TaskStatus} from '../../../tasks/enums/TaskStatus';
import {logger} from '../logger';
import {RequirementItemComponent} from './RequirementItemComponent';


// Use legacy service to locally handle per-item requests, since they are allowed to occur in parallel
const dependencies = {};
export const RequirementItemContainerInjector = buildInjector(dependencies, [
  'PersistentUploaderService',
  'EventBus',
]);

const FileStatus = {
  NO_CHANGE: 'NO_CHANGE',
  START: 'START',
  COMPLETE: 'COMPLETE',
};

const StreamedComponent = (props$) => {
  const PersistentUploaderService = dependencies.PersistentUploaderService;
  const EventBus = dependencies.EventBus;

  const init$ = buildInit$(props$);

  const uploadCount$ = init$
    .switchMap(() =>
      props$
        .map(props => getId(props.model))
        .distinctUntilChanged()
        .switchMap(id => !id
          ? Observable.empty()
          : PersistentUploaderService.size$()
            .map(() => PersistentUploaderService.getTotalByKey(id))
            .distinctUntilChanged()
        )
        .catch(epicErrorHandler(logger, 'state$'))
    )
    .takeUntil(props$.last())
    .publishReplay(1)
    .refCount();

  const status$ = props$
    .map(props => get(props.model, 'status'))
    .distinctUntilChanged()
    .takeUntil(props$.last())
    .publishReplay(1)
    .refCount();

  // When the upload count goes to zero, we need to watch the req status change from pending to anything else.
  // While in the isWorking state, we want to force the component to display the loading spinner.
  // If the req status is already something other than pending, we do not need to use the isWorking state, and can immediately revert from the spinner, to the status display.
  // If a file upload error occurs for this requirement, we need to also abort and display the status again.
  const workingState$ = uploadCount$
    .startWith(0)
    .pairwise()
    .map(([prevUploadCount, nextUploadCount]) => nextUploadCount === 0 && prevUploadCount > 0)
    .startWith(false)
    .distinctUntilChanged()
    .mergeMap(isWorking => {
      if (!isWorking) {
        return Observable.of({
          isWorking: false,
          status: FileStatus.NO_CHANGE,
        });
      } else {
        return Observable
          .merge(
            Observable.of({
              isWorking: true,
              status: FileStatus.START,
            }),
            Observable
              .merge(
                status$.filter(status => status !== TaskStatus.pending),

                props$
                  .map(props => getId(props.model))
                  .distinctUntilChanged()
                  .switchMap(id =>
                    new Observable(observer =>
                      EventBus.on(GlobalEvents.FileUploadError, (_, data) => observer.next(data))
                    )
                      .filter(ev => ev.modelId === id)
                  ),

                Observable.timer(240 * 1000), // 4 minute timeout
              )
              .take(1)
              .mapTo({
                isWorking: false,
                status: FileStatus.COMPLETE,
              })
          );
      }
    })
    .startWith({
      isWorking: false,
      status: FileStatus.NO_CHANGE,
    })
    .scan((acc, cur) => {
      switch (cur.status) {
        case FileStatus.NO_CHANGE:
          // handle initial case and any time there is no change to report
          return isObject(acc) ? acc : cur;
        case FileStatus.START:
        case FileStatus.COMPLETE:
          return cur;
      }
    })
    .takeUntil(props$.last());

  return props$
    .combineLatest(
      // Work when props.init is false
      uploadCount$.startWith(0),
      workingState$,
      (props, uploadCount, workingState) => ({
        ...props,
        uploadCount: workingState.isWorking ? 1 : uploadCount,
      }));
};

export const RequirementItemContainer = mapPropsStream(StreamedComponent)(RequirementItemComponent);

RequirementItemContainer.messages = RequirementItemComponent.messages;
RequirementItemContainer.messageProps = RequirementItemComponent.messageProps;



// WEBPACK FOOTER //
// ./src/js/requirements/components/RequirementItem/RequirementItemContainer.js